const GRAPHQL_PROXY_PATH = '/api/graphql/'

const HTTP_PROTOCOL = /^https?:\/\//

export const getConfiguredBackendGraphqlEndpoint = () => {
  const configuredEndpoint = process.env.GRAPHQL_ENDPOINT?.trim()

  if (configuredEndpoint) {
    return configuredEndpoint
  }

  return null
}

const getShopOrigin = () => {
  const shopUrl = process.env.NEXT_PUBLIC_SHOP_URL

  if (!shopUrl) {
    throw new Error('NEXT_PUBLIC_SHOP_URL is not set in the environment variables')
  }

  return shopUrl
}

const toAbsoluteGraphqlEndpoint = (endpoint: string) => {
  if (HTTP_PROTOCOL.test(endpoint)) {
    return endpoint
  }

  return new URL(endpoint, getShopOrigin()).toString()
}

const DEFAULT_BROWSER_BACKEND_BASE = '/backend'

/**
 * Base for backend URLs the browser loads directly (e.g. QR-code images on the
 * booking detail pages), derived from GRAPHQL_ENDPOINT minus its `/graphql`.
 *
 * In production shop and backend share an origin (7f.com/backend/* is routed
 * to the backend), so this stays the relative `/backend`. On staging the
 * backend is only reachable on its own host, so the base becomes absolute
 * (https://backend.staging.7fridays.io/backend) — a relative URL would hit
 * the shop and 404. next.config exposes the result to client code as
 * `process.env.BROWSER_BACKEND_BASE`.
 */
export const resolveBrowserBackendBase = (
  graphqlEndpoint: string | null | undefined,
  shopUrl: string | undefined
) => {
  if (!graphqlEndpoint) {
    return DEFAULT_BROWSER_BACKEND_BASE
  }

  let endpoint: URL
  try {
    endpoint = new URL(graphqlEndpoint, shopUrl)
  } catch {
    return DEFAULT_BROWSER_BACKEND_BASE
  }

  const basePath = endpoint.pathname.replace(/\/+$/, '').replace(/\/graphql$/, '')

  let shopOrigin: string | null = null
  try {
    shopOrigin = shopUrl ? new URL(shopUrl).origin : null
  } catch {
    shopOrigin = null
  }

  return shopOrigin === endpoint.origin ? basePath : `${endpoint.origin}${basePath}`
}

export const getBrowserGraphqlEndpoint = () => {
  return GRAPHQL_PROXY_PATH
}

export const getServerGraphqlEndpoint = () => {
  const endpoint = getConfiguredBackendGraphqlEndpoint()
  if (!endpoint) {
    throw new Error('GRAPHQL_ENDPOINT must be configured for server-side GraphQL requests')
  }

  return toAbsoluteGraphqlEndpoint(endpoint)
}
